import * as utils from "../utils/utils";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Button from "../components/common/Button";
import { Link } from "react-router-dom";
import { CircleCheck } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/common/Card";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import Alert from "../components/common/Alert";
import { RadioGroup, RadioGroupItem } from "../components/common/RadioGroup";
import { Checkbox } from "../components/common/Checkbox";
import { Dialog, DialogContent, DialogClose } from "../components/common/Dialog";
import { useAuth } from "../lib/AuthContext";
import {
  AlertCircle,
  ArrowLeft,
  Loader2,
  Stethoscope,
  Building2,
  User,
  X,
} from "lucide-react";

import config from "../config";
import useBrandTranslation from "../hooks/useBrandTranslation";
import LanguageSwitcher from "./common/LanguageSwitcher";
import AuthPageLoader from "./common/AuthPageLoader";
import { tr } from "../i18n/translate";
import { apiJsonHeaders, withApiLocale } from "../utils/apiLocale";

export default function RegisterForm() {
  const { t, language } = useBrandTranslation();
  const navigation = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const isMedicalBrand = config?.brand?.toUpperCase() === "ARISTAS" || config?.brand?.toUpperCase() === "BINAPSIS";
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    last_name: "",
    role: "patient", // Default role
    phone: "",
    specialization: "",
    hospital_name: "",
    private_appointment_price: "0.00",
    license_number: "",
    password: "",
    confirmPassword: "",
    acceptedTerms: false,
    consentMarketing: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(""); // Clear error when user makes changes
    setSuccess("");
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
    setError("");
    setSuccess("");
  };

  const handleRoleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      role: value,
      // Limpiar campos específicos cuando cambia el rol
      specialization: "",
      license_number: "",
      hospital_name: "",
      private_appointment_price: "",
    }));
    setError("");
    setSuccess("");
  };

  const validateForm = () => {
    if (!formData.email.trim()) return tr("app.register.emailRequired", "El email es obligatorio");
    if (!formData.email.includes("@")) return tr("app.register.emailInvalid", "El email no es válido");
    if (!formData.name.trim()) return tr("app.register.nameRequired", "El nombre es obligatorio");
    if (!formData.last_name.trim()) return tr("app.register.lastNameRequired", "El apellido es obligatorio");
    if (!formData.phone.trim()) return tr("app.register.phoneRequired", "El teléfono es obligatorio");
    if (formData.password.length < 6)
      return tr("app.register.passwordMin", "La contraseña debe tener al menos 6 caracteres");
    if (formData.password !== formData.confirmPassword)
      return tr("app.register.passwordMismatch", "Las contraseñas no coinciden");
    if (!formData.acceptedTerms)
      return tr("app.register.termsRequired", "Debes aceptar los términos y condiciones para continuar");

    if (formData.role === "doctor") {
      if (!formData.specialization.trim())
        return tr("app.register.specializationRequired", "La especialización es obligatoria para médicos");
      if (!formData.license_number.trim())
        return tr("app.register.licenseRequired", "La matrícula es obligatoria para profesionales");
    }

    if (formData.role === "institution" || formData.role === "association") {
      if (!formData.hospital_name.trim())
        return tr("app.register.orgNameRequired", "El nombre del hospital/institución/asociación es obligatorio");
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log("🔐 Iniciando registro para:", formData.email);

      const typeMapping = {
        patient: "USER",
        doctor: "PROFESSIONAL",
        institution: "INSTITUTION",
        secretary: "SECRETARY",
        association: "ASSOCIATION",
      };

      const payload = {
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim(),
        role: formData.role,
        type: typeMapping[formData.role] || formData.role,
        username: formData.email.trim(),
        specialization:
          formData.role === "doctor"
            ? formData.specialization.trim()
            : undefined,
        hospital_name:
          formData.role === "institution" || formData.role === "association"
            ? formData.hospital_name.trim()
            : undefined,
        private_appointment_price:
          formData.role === "doctor"
            ? Number(formData.private_appointment_price || 0)
            : undefined,
        accepted_terms: formData.acceptedTerms,
        consent_marketing: formData.consentMarketing,
        license_number:
          formData.role === "doctor"
            ? formData.license_number.trim()
            : undefined,
        locale: language,
      };

      const response = await fetch(`${config.baseUrlApi}/users/register`, {
        method: "POST",
        headers: apiJsonHeaders(),
        body: JSON.stringify(payload),
      });

      let result = {};
      try {
        result = await response.json();
      } catch (parseError) {
        console.error("⚠️ No se pudo parsear la respuesta JSON:", parseError);
        if (!response.ok) {
          throw new Error(tr("app.register.createError", "Error al crear el usuario. Intente nuevamente."));
        }
      }

      const apiErrorRaw =
        result?.error ??
        (!response.ok ? result?.message ?? result?.detail : undefined);
      const apiError =
        typeof apiErrorRaw === "string"
          ? apiErrorRaw.trim()
          : apiErrorRaw && typeof apiErrorRaw === "object"
          ? (apiErrorRaw.message || "").trim()
          : "";

      if (result?.error === "REGISTRATION_NOT_ALLOWED") {
        throw new Error(
          result?.message ||
            tr("app.register.registrationNotAllowed", "Próximamente podrás utilizar este mail para registrarte en nuestra plataforma")
        );
      }

      if (!response.ok || apiError) {
        const msg =
          (typeof apiError === "string" ? apiError.trim() : "") ||
          tr("app.register.createError", "Error al crear el usuario. Intente nuevamente.");
        throw new Error(msg);
      }

      console.log("✅ Usuario creado mediante API:", result);

      // PASO 2: Mostrar mensaje de éxito
      const roleText =
        formData.role === "doctor"
          ? tr("app.register.roleDoctorShort", "médico")
          : formData.role === "institution"
          ? tr("app.register.roleInstitutionShort", "institución")
          : formData.role === "association"
          ? tr("app.register.roleAssociationShort", "asociación")
          : formData.role === "secretary"
          ? tr("app.register.roleSecretaryShort", "secretaria")
          : tr("app.register.rolePatientShort", "paciente");
      const successMessage =
        formData.role === "doctor"
          ? tr("app.register.successProfessional", "¡Cuenta de profesional creada exitosamente! Te redirigimos al menú principal...")
          : tr("app.register.successRole", "¡Cuenta de {{role}} creada exitosamente! Iniciando sesión...", { role: roleText });
      setSuccess(successMessage);

      // Auto-login después del registro
      setTimeout(async () => {
        console.log("🔄 Intentando auto-login...");
    const loginResult = await login(
          formData.email.trim(),
          formData.password
        );
        if (loginResult?.success) {
          navigation("/");
        } else {
          setSuccess(
            tr("app.register.successManualLogin", "Cuenta creada exitosamente. Por favor, inicia sesión manualmente.")
          );
        }
      }, 2000);
    } catch (err) {
      console.error("💥 Error completo en registro:", err);

      // Mensajes de error más específicos
      let errorMessage =
        typeof err.message === "string" ? err.message.trim() : "";

      if (
        err.message?.includes("User already registered") ||
        err.message?.includes("ya se encuentra registrado") ||
        err.message?.includes("email ya está registrado")
      ) {
        errorMessage = tr("app.register.emailExists", "Este email ya está registrado. Intenta iniciar sesión.");
      } else if (err.message?.includes("Password should be at least")) {
        errorMessage = tr("app.register.passwordMin", "La contraseña debe tener al menos 6 caracteres");
      } else if (err.message?.includes("Invalid email")) {
        errorMessage = tr("app.register.invalidEmailFormat", "El formato del email no es válido.");
      }

      if (!errorMessage || errorMessage === '""') {
        errorMessage = tr("app.register.genericError", "Error al registrarse. Intente nuevamente.");
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      doctor: t("app.register.roleDoctorLabel", "Médico"),
      patient: t("app.register.rolePatientLabel", "Paciente"),
      institution: t("app.register.roleInstitutionLabel", "Institución"),
      secretary: t("app.register.roleSecretaryLabel", "Secretaria"),
      association: t("app.register.roleAssociationLabel", "Asociación"),
    };
    return labels[role] || role;
  };

  const termsSections = [
    {
      titleKey: "acceptanceTitle",
      bodyKey: "acceptanceBody",
      titleDefault: "1. Aceptación de los Términos",
      bodyDefault:
        "Al acceder y utilizar esta plataforma, usted acepta estar sujeto a estos términos y condiciones de uso. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestros servicios.",
    },
    {
      titleKey: "serviceTitle",
      bodyKey: "serviceBody",
      titleDefault: "2. Uso del Servicio",
      bodyDefault:
        "Usted se compromete a utilizar la plataforma únicamente para fines legales y de acuerdo con estos términos. No debe utilizar el servicio de manera que pueda dañar, deshabilitar, sobrecargar o comprometer nuestros sistemas.",
    },
    {
      titleKey: "accountTitle",
      bodyKey: "accountBody",
      titleDefault: "3. Cuenta de Usuario",
      bodyDefault:
        "Es responsable de mantener la confidencialidad de su cuenta y contraseña. Usted acepta la responsabilidad de todas las actividades que ocurran bajo su cuenta. Debe notificarnos inmediatamente de cualquier uso no autorizado de su cuenta.",
    },
    {
      titleKey: "privacyTitle",
      bodyKey: "privacyBody",
      titleDefault: "4. Privacidad y Protección de Datos",
      bodyDefault:
        "Respetamos su privacidad y protegemos sus datos personales de acuerdo con nuestra política de privacidad. Al utilizar nuestros servicios, usted consiente la recopilación y el uso de información de acuerdo con dicha política.",
    },
    {
      titleKey: "responsibilitiesTitle",
      bodyKey: "responsibilitiesBody",
      titleDefault: "5. Responsabilidades del Usuario",
      bodyDefault:
        "Usted es responsable de proporcionar información precisa y actualizada. No debe proporcionar información falsa, engañosa o fraudulenta. Cualquier violación de estos términos puede resultar en la terminación inmediata de su cuenta.",
    },
    {
      titleKey: "liabilityTitle",
      bodyKey: "liabilityBody",
      titleDefault: "6. Limitación de Responsabilidad",
      bodyDefault:
        "En la medida máxima permitida por la ley, no seremos responsables de ningún daño directo, indirecto, incidental, especial o consecuente que resulte del uso o la imposibilidad de usar nuestros servicios.",
    },
    {
      titleKey: "modificationsTitle",
      bodyKey: "modificationsBody",
      titleDefault: "7. Modificaciones de los Términos",
      bodyDefault:
        "Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación. Su uso continuado del servicio después de dichas modificaciones constituye su aceptación de los nuevos términos.",
    },
    {
      titleKey: "contactTitle",
      bodyKey: "contactBody",
      titleDefault: "8. Contacto",
      bodyDefault:
        "Si tiene alguna pregunta sobre estos términos y condiciones, puede contactarnos a través de los medios proporcionados en nuestra plataforma.",
    },
  ];

  const getRoleIcon = (role) => {
    switch (role) {
      case "doctor":
        return <Stethoscope className="w-4 h-4" />;
      case "institution":
        return <Building2 className="w-4 h-4" />;
      case "association":
        return <Building2 className="w-4 h-4" />;
      case "secretary":
        return <Building2 className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case "doctor":
        return t("app.register.roleDoctor", "Profesional médico independiente");
      case "institution":
        return t("app.register.roleInstitution", "Hospital, clínica o centro médico");
      case "association":
        return t("app.register.roleAssociation", "Asociación médica o profesional");
      case "secretary":
        return t("app.register.roleSecretary", "Secretaria de una institución médica");
      default:
        return t("app.register.rolePatient", "Persona que busca atención médica");
    }
  };

  if (isLoading) {
    return (
      <AuthPageLoader
        message={t("app.register.creatingAccount", "Creando cuenta...")}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-lg space-y-4">
        <div className="flex justify-between items-center">
          <a
            href="/"
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("common.backToHome", "Volver al inicio")}
          </a>
        </div>

        <div className="flex justify-center mb-3">
          <LanguageSwitcher variant="dark" />
        </div>

        <div className="flex items-center justify-center mb-4">
                <img
                  src={utils.getBrandLogoSrc()}
                  alt="logo"
                  className={utils.getBrandLogoClassName()}
                />
                {!utils.isBinapsisBrand() && (
                  <h1 className="inline-block text-2xl sm:text-3xl text-gray-400 pl-2 tracking-tight ">
                    {config.brand}
                  </h1>
                )}
              </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("auth.createAccount", "Crear Cuenta")}</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div
                className="mb-4 rounded-lg border-2 border-red-500 bg-red-50 p-4 text-red-800"
                role="alert"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <p className="whitespace-pre-line text-sm font-medium">
                    {error}
                  </p>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-4">
                <Alert text={success} intent="success" fullWidth />
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Información básica */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t("app.register.personalInfo", "Información Personal")}</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("app.register.firstName", "Nombre *")}</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t("app.register.yourName", "Tu nombre")}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">{t("app.register.lastName", "Apellido *")}</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder={t("app.register.yourLastName", "Tu apellido")}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("app.register.email", "Email *")}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("app.register.emailPlaceholder", "tu@email.com")}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t("app.register.phone", "Teléfono *")}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t("app.register.phonePlaceholderExample", "+54 11 1234-5678")}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {isMedicalBrand && (
                <>
                  {/* Tipo de cuenta */}
                  <div className="space-y-3">
                    <Label>{t("app.register.accountType", "Tipo de cuenta *")}</Label>
                    <RadioGroup
                      value={formData.role}
                      onValueChange={handleRoleChange}
                      className="space-y-3"
                      disabled={isLoading}
                    >
                      <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem
                          value="patient"
                          id="patient"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="patient"
                            className="flex items-center gap-2 font-medium cursor-pointer"
                          >
                            {getRoleIcon("patient")}
                            {t("app.register.rolePatientLabel", "Paciente")}
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            {getRoleDescription("patient")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem
                          value="doctor"
                          id="doctor"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="doctor"
                            className="flex items-center gap-2 font-medium cursor-pointer"
                          >
                            {getRoleIcon("doctor")}
                            {t("app.register.roleDoctorLabel", "Médico")}
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            {getRoleDescription("doctor")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem
                          value="institution"
                          id="institution"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="institution"
                            className="flex items-center gap-2 font-medium cursor-pointer"
                          >
                            {getRoleIcon("institution")}
                            {t("app.register.roleInstitutionLabel", "Institución")}
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            {getRoleDescription("institution")}
                          </p>
                        </div>
                      </div>

                      {/* <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem
                          value="association"
                          id="association"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="association"
                            className="flex items-center gap-2 font-medium cursor-pointer"
                          >
                            {getRoleIcon("association")}
                            Asociación
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            {getRoleDescription("association")}
                          </p>
                        </div>
                      </div> */}

                      {/* <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem
                          value="secretary"
                          id="secretary"
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="secretary"
                            className="flex items-center gap-2 font-medium cursor-pointer"
                          >
                            {getRoleIcon("secretary")}
                            Secretaria
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            {getRoleDescription("secretary")}
                          </p>
                        </div>
                      </div> */}
                    </RadioGroup>
                  </div>
                </>
              )}

              {/* Campos específicos para médicos */}
              {formData.role === "doctor" && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900">
                    {t("app.register.professionalInfo", "Información Profesional")}
                  </h4>

                  <div className="space-y-2">
                    <Label htmlFor="specialization">{t("app.register.specialization", "Especialización *")}</Label>
                    <Input
                      id="specialization"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      placeholder={t("app.register.specializationPlaceholder", "Ej: Cardiología, Pediatría, Medicina General")}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="license_number">
                      {t("app.register.licenseNumber", "Número de matrícula *")}
                    </Label>
                    <Input
                      id="license_number"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleChange}
                      placeholder={t("app.register.licensePlaceholder", "Ej: MN 123456")}
                      required
                      disabled={isLoading}
                    />
                  </div>

                </div>
              )}

              {/* Campos específicos para instituciones y asociaciones */}
              {(formData.role === "institution" || formData.role === "association") && (
                <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-900">
                    {formData.role === "association" ? t("app.register.associationInfo", "Información de la Asociación") : t("app.register.institutionInfo", "Información Institucional")}
                  </h4>

                  <div className="space-y-2">
                    <Label htmlFor="hospital_name">
                      {formData.role === "association" ? t("app.register.associationName", "Nombre de la Asociación *") : t("app.register.hospitalName", "Nombre del Hospital/Clínica *")}
                    </Label>
                    <Input
                      id="hospital_name"
                      name="hospital_name"
                      value={formData.hospital_name}
                      onChange={handleChange}
                      placeholder={t("app.register.orgPlaceholder", "Ej: Hospital Central, Clínica San José")}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )}

              {/* Contraseña */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t("app.register.security", "Seguridad")}</h3>

                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t("app.register.password", "Contraseña *")}</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={t("app.register.passwordPlaceholder", "Mínimo 6 caracteres")}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      {t("app.register.confirmPassword", "Confirmar contraseña *")}
                    </Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder={t("app.register.confirmPasswordPlaceholder", "Repetir contraseña")}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Términos y condiciones y consentimiento */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t("app.register.termsSection", "Términos y Consentimiento")}</h3>

                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="acceptedTerms"
                      checked={formData.acceptedTerms}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange("acceptedTerms", checked)
                      }
                      disabled={isLoading}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="acceptedTerms"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("app.register.acceptTermsPrefix", "Acepto los")}{" "}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsTermsModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {t("app.register.termsLink", "términos y condiciones")}
                        </button>{" "}
                        *
                      </Label>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consentMarketing"
                      checked={formData.consentMarketing}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange("consentMarketing", checked)
                      }
                      disabled={isLoading}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="consentMarketing"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t(
                          "app.register.consentMarketing",
                          "Acepto recibir mensajes de texto y/o correos electrónicos"
                        )}
                      </Label>
                    </div>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("app.register.creatingAccount", "Creando cuenta...")}
                  </>
                ) : isMedicalBrand ? (
                  <>
                    {getRoleIcon(formData.role)}
                    <span className="ml-2">
                      {t("app.register.createAccountAs", "Crear cuenta como {{role}}", {
                        role: getRoleLabel(formData.role),
                      })}
                    </span>
                  </>
                ) : (
                  <span>{t("app.register.submit", "Registrarse")}</span>
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-gray-600">
              {t("app.register.hasAccount", "¿Ya tenés cuenta?")}{" "}
              <a href="/login" className="text-blue-600 hover:underline">
                {t("app.register.loginLink", "Iniciá sesión")}
              </a>
            </p>
          </CardFooter>
        </Card>

        {/* Modal de Términos y Condiciones */}
        <Dialog open={isTermsModalOpen} onOpenChange={setIsTermsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{t("app.register.termsTitle", "Términos y Condiciones")}</h2>
              <DialogClose asChild>
                <button
                  onClick={() => setIsTermsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label={t("common.close", "Cerrar")}
                >
                  <X className="w-6 h-6" />
                </button>
              </DialogClose>
            </div>
            <div className="overflow-y-auto pr-2 space-y-4 text-sm text-gray-700">
              {termsSections.map(({ titleKey, bodyKey, titleDefault, bodyDefault }) => (
                <section key={titleKey}>
                  <h3 className="font-semibold text-lg mb-2">
                    {t(`app.register.termsLegal.${titleKey}`, titleDefault)}
                  </h3>
                  <p>{t(`app.register.termsLegal.${bodyKey}`, bodyDefault)}</p>
                </section>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setIsTermsModalOpen(false)}>
                {t("common.close", "Cerrar")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
